// Visual size selector for products that offer multiple cake sizes.
// Each option ships with a clean SVG icon (round vs square top-down view).
//
// The four canonical options (set in admin per product):
//   6_round, 8_round, 10_round, 12_square

const SHAPE = {
  '6_round':  { label: '6" Round',  shape: 'round'  },
  '8_round':  { label: '8" Round',  shape: 'round'  },
  '10_round': { label: '10" Round', shape: 'round'  },
  '12_square':{ label: '12" Square',shape: 'square' },
};

// Order in which we want sizes to render (smallest → largest)
export const CANONICAL_SIZE_ORDER = ['6_round', '8_round', '10_round', '12_square', '6_pack', '12_pack', '24_pack'];

const RoundIcon = ({ active }) => (
  <svg viewBox="0 0 80 64" width="80" height="64" aria-hidden="true">
    {/* Plate */}
    <ellipse cx="40" cy="48" rx="34" ry="6" fill={active ? '#FFFBEF' : '#F5F1EA'} />
    {/* Cake body (cylinder) */}
    <path
      d="M10 26 v18 a30 8 0 0 0 60 0 v-18 z"
      fill={active ? '#1a1a1a' : '#2a2a2a'}
    />
    {/* Top oval */}
    <ellipse cx="40" cy="26" rx="30" ry="8" fill={active ? '#D4AF37' : '#8D6E63'} />
    {/* Drip / icing edge */}
    <path
      d="M10 28 q8 10 16 0 t16 0 t16 0 t12 0 v6 a30 8 0 0 1 -60 0 z"
      fill={active ? '#1a1a1a' : '#1a1a1a'}
      opacity="0.4"
    />
    {/* Topping dot */}
    <circle cx="40" cy="22" r="3" fill="#fff" opacity="0.9" />
  </svg>
);

// Single cupcake — used as a small motif for the box-of-N icon
const CupcakeMotif = ({ active, cx, cy }) => (
  <g transform={`translate(${cx - 8} ${cy - 10})`}>
    {/* Wrapper (trapezoid) */}
    <path d="M2 8 L14 8 L12 18 L4 18 Z" fill={active ? '#1a1a1a' : '#2a2a2a'} />
    {/* Cream swirl */}
    <path d="M2 8 Q4 2 8 4 Q12 1 14 8 Z" fill={active ? '#D4AF37' : '#8D6E63'} />
    {/* Cherry */}
    <circle cx="8" cy="3" r="1.5" fill="#E5484D" />
  </g>
);

const CupcakeBoxIcon = ({ active, count = 12 }) => {
  // Determine a tidy grid for the box (rows × cols)
  const grid = count <= 6 ? { rows: 2, cols: 3 }
    : count <= 12 ? { rows: 3, cols: 4 }
    : { rows: 4, cols: 6 };
  const cellW = 68 / grid.cols;
  const cellH = 38 / grid.rows;
  const dots = [];
  for (let r = 0; r < grid.rows; r++) {
    for (let c = 0; c < grid.cols; c++) {
      if (dots.length >= count) break;
      dots.push({
        cx: 6 + c * cellW + cellW / 2,
        cy: 14 + r * cellH + cellH / 2,
      });
    }
  }
  return (
    <svg viewBox="0 0 80 64" width="80" height="64" aria-hidden="true">
      {/* Box top edge / shadow */}
      <ellipse cx="40" cy="56" rx="32" ry="4" fill={active ? '#FFFBEF' : '#F5F1EA'} />
      {/* Box body */}
      <rect x="4" y="10" width="72" height="44" rx="4" fill={active ? '#FFFBEF' : '#FAFAF9'} stroke={active ? '#D4AF37' : '#8D6E63'} strokeWidth="2" />
      {/* Cupcake dots */}
      {dots.map((d, i) => (
        <CupcakeMotif key={i} active={active} cx={d.cx} cy={d.cy} />
      ))}
    </svg>
  );
};

const SquareIcon = ({ active }) => (
  <svg viewBox="0 0 80 64" width="80" height="64" aria-hidden="true">
    {/* Plate */}
    <ellipse cx="40" cy="50" rx="34" ry="5" fill={active ? '#FFFBEF' : '#F5F1EA'} />
    {/* Cake body (3-D box) */}
    <polygon
      points="14,28 66,28 66,46 14,46"
      fill={active ? '#1a1a1a' : '#2a2a2a'}
    />
    <polygon
      points="14,28 22,22 74,22 66,28"
      fill={active ? '#D4AF37' : '#8D6E63'}
    />
    <polygon
      points="66,28 74,22 74,40 66,46"
      fill={active ? '#1a1a1a' : '#3a3a3a'}
      opacity="0.9"
    />
    {/* Topping dots */}
    <circle cx="32" cy="24" r="2" fill="#fff" opacity="0.85" />
    <circle cx="48" cy="24" r="2" fill="#fff" opacity="0.85" />
  </svg>
);

const Icon = ({ shape, active, count }) => {
  if (shape === 'cupcake_box') return <CupcakeBoxIcon active={active} count={count} />;
  if (shape === 'square') return <SquareIcon active={active} />;
  return <RoundIcon active={active} />;
};

// Try to parse a quantity (e.g. 6, 12, 24) from a size's id or name when the product
// represents a multi-pack rather than a cake diameter.
const parseCount = (size) => {
  const candidates = [size.id, size.name].filter(Boolean);
  for (const s of candidates) {
    const m = String(s).match(/\d+/);
    if (m) return parseInt(m[0], 10);
  }
  return 12;
};

export default function SizeSelector({ sizes, basePrice = 0, value, onChange, label = 'Choose Your Size', variant = 'cake' }) {
  if (!sizes || sizes.length === 0) return null;

  // Sort according to the canonical order so 6 → 8 → 10 → 12 always
  const ordered = [...sizes].sort(
    (a, b) => CANONICAL_SIZE_ORDER.indexOf(a.id) - CANONICAL_SIZE_ORDER.indexOf(b.id)
  );

  return (
    <div className="space-y-3" data-testid="size-selector">
      <div className="flex items-baseline justify-between">
        <span className="text-sm font-medium text-[#1a1a1a]">{label}</span>
        <span className="text-xs text-stone-500">Select one</span>
      </div>

      <div className={`grid gap-3 ${ordered.length >= 3 ? 'grid-cols-2 sm:grid-cols-' + ordered.length : 'grid-cols-' + ordered.length}`}>
        {ordered.map((size) => {
          const baseMeta = SHAPE[size.id] || { label: size.name || size.id, shape: 'round' };
          // If product variant is cupcake_box, override the shape regardless of size id
          const isPackVariant = variant === 'cupcake_box';
          const meta = {
            ...baseMeta,
            label: size.name || baseMeta.label,
            shape: isPackVariant ? 'cupcake_box' : baseMeta.shape,
          };
          const active = value === size.id;
          const price = (basePrice || 0) + (size.price_modifier || 0);
          const count = isPackVariant ? parseCount(size) : null;

          return (
            <button
              type="button"
              key={size.id}
              onClick={() => onChange(size.id)}
              data-testid={`size-card-${size.id}`}
              className={`group relative overflow-hidden rounded-2xl border-2 px-3 py-4 text-center transition-all ${
                active
                  ? 'border-[#D4AF37] bg-[#FFFBEF] shadow-[0_4px_18px_rgba(212,175,55,0.25)]'
                  : 'border-stone-200 bg-white hover:border-[#8D6E63]/40 hover:bg-stone-50'
              }`}
            >
              {/* Selected check badge */}
              {active && (
                <span className="absolute top-2 right-2 w-5 h-5 rounded-full bg-[#1a1a1a] text-[#D4AF37] text-[10px] font-bold flex items-center justify-center">
                  ✓
                </span>
              )}

              <div className="flex items-center justify-center mb-2">
                <Icon shape={meta.shape} active={active} count={count} />
              </div>

              <div className={`font-['Playfair_Display'] text-base font-bold ${active ? 'text-[#1a1a1a]' : 'text-stone-700'}`}>
                {meta.label}
              </div>

              <div className={`mt-1 text-sm font-semibold ${active ? 'text-[#8D6E63]' : 'text-stone-500'}`}>
                €{price.toFixed(2)}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
